import { AuthenticationEntity } from "@modules/auth/entities/auth.entity"
import { API_HEADER, API_URL } from "@modules/shared/enums/common-enum"
import { fetchWithAuth } from "@modules/shared/helpers/fetch-with-auth.helper"

export const SystemSettingService = {
  /*
   *  ==========================================================
   *     AI Assistant
   *  ==========================================================
   */
  async getAIAssistantStatus(
    auth: AuthenticationEntity,
    setAuth: (auth: AuthenticationEntity | null) => void,
    logout: () => void
  ) {
    let res = await fetchWithAuth(
      `${API_URL.AI_ASSISTANT_STATUS}`,
      {
        method: "GET",
        headers: {
          ...API_HEADER,
          'x-platform': "web"
        }
      },
      auth,
      setAuth,
      logout
    );

    return res;
  },

  async getAIAssistantId(
    auth: AuthenticationEntity,
    setAuth: (auth: AuthenticationEntity | null) => void,
    logout: () => void
  ) {
    let res = await fetchWithAuth(
      `${API_URL.AI_ASSISTANT_ID}`,
      {
        method: "GET",
        headers: {
          ...API_HEADER,
          'x-platform': 'web'
        }
      },
      auth,
      setAuth,
      logout
    );
    return res;
  },

  async getVectorStorageId(
    auth: AuthenticationEntity,
    setAuth: (auth: AuthenticationEntity | null) => void,
    logout: () => void
  ) {
    let res = await fetchWithAuth(
      `${API_URL.AI_VECTOR_STORAGE_ID}`,
      {
        method: "GET",
        headers: {
          ...API_HEADER,
          'x-platform': 'web'
        }
      },
      auth,
      setAuth,
      logout
    );
    return res;
  },

  async updateAIAssistant(
    auth: AuthenticationEntity,
    setAuth: (auth: AuthenticationEntity | null) => void,
    logout: () => void,
    instruction?: string,
    model?: string,
    vectorStorageId?: string
  ) {
    let res = await fetchWithAuth(
      `${API_URL.AI_ASSISTANT_UPDATE}`,
      {
        method: "POST",
        headers: {
          ...API_HEADER,
          'x-platform': 'web'
        },
        body: JSON.stringify({
          instruction,
          model,
          'vector_storage_id': vectorStorageId
        })
      },
      auth,
      setAuth,
      logout
    );
    return res;
  },

  async updateVectorStorage(
    auth: AuthenticationEntity,
    setAuth: (auth: AuthenticationEntity | null) => void,
    logout: () => void,
    vectorStorageId: string
  ) {
    let res = await fetchWithAuth(
      `${API_URL.AI_VECTOR_STORAGE_ID}`,
      {
        method: "PUT",
        headers: {
          ...API_HEADER,
          'x-platform': 'web'
        },
        body: JSON.stringify({
          'vector_storage_id': vectorStorageId
        })
      },
      auth,
      setAuth,
      logout
    );
    return res;
  },

  async initialAIAssistant(
    auth: AuthenticationEntity,
    setAuth: (auth: AuthenticationEntity | null) => void,
    logout: () => void
  ) {
    let res = await fetchWithAuth(
      `${API_URL.AI_ASSISTANT_INITIAL}`,
      {
        method: "POST",
        headers: {
          ...API_HEADER,
          'x-platform': 'web'
        }
      },
      auth,
      setAuth,
      logout
    );
    return res;
  },

  /*
   *  ==========================================================
   *    MongoDB
   *  ==========================================================
   */

  async removeFollowDuplicate(
    auth: AuthenticationEntity,
    setAuth: (auth: AuthenticationEntity | null) => void,
    logout: () => void
  ) {
    let res = await fetchWithAuth(
      `${API_URL.REMOVE_DUPE_FOLLOW}`,
      {
        method: "POST",
        headers: {
          ...API_HEADER,
          'x-platform': 'web'
        }
      },
      auth,
      setAuth,
      logout
    );
    return res;
  },

  async syncFollowIndexes(
    auth: AuthenticationEntity,
    setAuth: (auth: AuthenticationEntity | null) => void,
    logout: () => void
  ) {
    let res = await fetchWithAuth(
      `${API_URL.SYNC_INDEXES_FOLLOW}`,
      {
        method: "POST",
        headers: {
          ...API_HEADER,
          'x-platform': 'web'
        }
      },
      auth,
      setAuth,
      logout
    );
    return res;
  },

  async removeDupeConnect(
    auth: AuthenticationEntity,
    setAuth: (auth: AuthenticationEntity | null) => void,
    logout: () => void
  ) {
    let res = await fetchWithAuth(
      `${API_URL.REMOVE_DUPE_CONNECT}`,
      {
        method: "POST",
        headers: {
          ...API_HEADER,
          'x-platform': 'web'
        }
      },
      auth,
      setAuth,
      logout
    );
    return res;
  },

  async syncConnectIndexes(
    auth: AuthenticationEntity,
    setAuth: (auth: AuthenticationEntity | null) => void,
    logout: () => void
  ) {
    let res = await fetchWithAuth(
      `${API_URL.SYNC_INDEXES_CONNECT}`,
      {
        method: "POST",
        headers: {
          ...API_HEADER,
          'x-platform': 'web'
        }
      },
      auth,
      setAuth,
      logout
    );
    return res;
  },

  async removeDupeSocialReaction(
    auth: AuthenticationEntity,
    setAuth: (auth: AuthenticationEntity | null) => void,
    logout: () => void
  ) {
    let res = await fetchWithAuth(
      `${API_URL.REMOVE_DUPE_SOCIAL_REACTION}`,
      {
        method: "POST",
        headers: {
          ...API_HEADER,
          'x-platform': 'web'
        }
      },
      auth,
      setAuth,
      logout
    );
    return res;
  },

  async syncSocialReactionIndexes(
    auth: AuthenticationEntity,
    setAuth: (auth: AuthenticationEntity | null) => void,
    logout: () => void
  ) {
    let res = await fetchWithAuth(
      `${API_URL.SYNC_INDEXES_SOCIAL_REACTION}`,
      {
        method: "POST",
        headers: {
          ...API_HEADER,
          'x-platform': 'web'
        }
      },
      auth,
      setAuth,
      logout
    );
    return res;
  },

  async removeDupeCommunityReaction(
    auth: AuthenticationEntity,
    setAuth: (auth: AuthenticationEntity | null) => void,
    logout: () => void
  ) {
    let res = await fetchWithAuth(
      `${API_URL.REMOVE_DUPE_COMMUNITY_REACTION}`,
      {
        method: "POST",
        headers: {
          ...API_HEADER,
          'x-platform': 'web'
        }
      },
      auth,
      setAuth,
      logout
    );
    return res;
  },

  async syncIndexesCommunityReactions(
    auth: AuthenticationEntity,
    setAuth: (auth: AuthenticationEntity | null) => void,
    logout: () => void
  ) {
    let res = await fetchWithAuth(
      `${API_URL.SYNC_INDEXES_COMMUNITY_REACTION}`,
      {
        method: "POST",
        headers: {
          ...API_HEADER,
          'x-platform': 'web'
        }
      },
      auth,
      setAuth,
      logout
    );
    return res;
  },

  /*
   *  ==========================================================
   *     AI Assistant
   *  ==========================================================
   */

  async updateVietGuysToken(
    auth: AuthenticationEntity,
    setAuth: (auth: AuthenticationEntity | null) => void,
    logout: () => void,
    vietguysToken: string
  ) {
    let res = await fetchWithAuth(
      `${API_URL.SYNC_INDEXES_COMMUNITY_REACTION}`,
      {
        method: "POST",
        headers: {
          ...API_HEADER,
          'x-platform': 'web'
        },
        body: JSON.stringify({
          'refresh_token': vietguysToken
        })
      },
      auth,
      setAuth,
      logout
    );
    return res;
  },
}
